import React, { useMemo, useRef } from 'react';
import {
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { Indicator } from 'components/elements/Indicator';
import Tooltip from 'components/elements/Tooltip';
import { useFilterContext } from 'components/widgets/FilterToolbar/FilterContext';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';

interface ContentDropdownProps {
  title: string | React.ReactNode;
  filterCount?: number | string;
  children: React.ReactNode;
  errorMessage?: string;
}

const outerContainer = {
  width: '100%',
};

const accordionItem = {
  border: 'none',
};

const accordionButtonStyle = {
  paddingLeft: 'padding_8',
  paddingRight: 'padding_8',
  paddingY: 'padding_4',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  minHeight: '32px', // Consistent height regardless of indicator presence

  position: 'sticky',
  top: 0,
  background: Colors.White,
  zIndex: Z_INDEX_DICT.DROPDOWN,

  _hover: {
    background: Colors.ArmyGreen100,
  },
  '&:focus': {
    boxShadow: 'none',
  },
};

const chevronContainer = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  minWidth: 'fit-content', // Ensure consistent width
  height: '100%', // Match button height
};

const panelStyle = {
  paddingX: 0,
  paddingTop: 'padding_8',
  paddingBottom: 'padding_8',
};

const Dropdown = ({
  title,
  children,
  filterCount,
  errorMessage,
}: ContentDropdownProps) => {
  const filterContext = useFilterContext();
  // Use title as a stable key for index assignment
  // FilterDropdown passes children (usually a string) as title
  const titleKeyRef = useRef<string>('');
  if (!titleKeyRef.current) {
    if (typeof title === 'string') {
      titleKeyRef.current = title;
    } else if (React.isValidElement(title) && typeof title.props?.children === 'string') {
      titleKeyRef.current = title.props.children;
    } else {
      // Fallback: use a stable identifier based on title content
      titleKeyRef.current = `filter-${String(title)}`;
    }
  }
  // Check if we're in a FilterContext (controlled mode)
  const isControlled = filterContext.setOpenFilterIndex !== undefined;
  const isExpanded = isControlled
    ? filterContext.openFilterIndex === titleKeyRef.current
    : undefined;

  const formattedFilterCount = useMemo(() => {
    if (!filterCount) {
      return '';
    }
    if (Number(filterCount) === 0 || String(filterCount) === '0') {
      return '';
    }

    return String(filterCount);
  }, [filterCount]);

  const handleChange = (expandedIndex: number | number[] | undefined) => {
    if (isControlled && filterContext.setOpenFilterIndex) {
      // When using allowToggle with index as array, onChange receives an array
      // Since we only have one AccordionItem (at index 0), if we receive [0], it means opening
      // If we receive [], it means closing
      let isOpening = false;

      if (expandedIndex === undefined || expandedIndex === null) {
        isOpening = false;
      } else if (Array.isArray(expandedIndex)) {
        // Array contains indices of open AccordionItems - [0] means this item is opening
        isOpening = expandedIndex.includes(0);
      } else if (typeof expandedIndex === 'number') {
        // If we receive a number, it's the AccordionItem index (0 for our single item)
        isOpening = expandedIndex === 0;
      }

      if (isOpening) {
        // Opening this filter (this will close any other open filter)
        filterContext.setOpenFilterIndex(titleKeyRef.current);
      } else {
        // Closing this filter
        filterContext.setOpenFilterIndex(undefined);
      }
    }
  };

  const accordionIndex = useMemo(() => {
    if (!isControlled) {
      return undefined;
    }

    return isExpanded ? [0] : [];
  }, [isControlled, isExpanded]);

  return (
    <Box sx={outerContainer}>
      <Accordion
        allowToggle
        index={accordionIndex}
        onChange={isControlled ? handleChange : undefined}
      >
        <AccordionItem sx={accordionItem}>
          {({ isExpanded: localIsExpanded }) => {
            const expanded = isControlled ? isExpanded : localIsExpanded;

            return (
              <>
                <AccordionButton sx={accordionButtonStyle}>
                  <Text.BodyBold>{title}</Text.BodyBold>
                  <Box sx={chevronContainer}>
                    {errorMessage && (
                      <Tooltip tooltipText={errorMessage}>
                        <Icon
                          name={Icon.Name.InfoExclamationMark}
                          fill={Colors.SystemColorNegative}
                        />
                      </Tooltip>
                    )}
                    {formattedFilterCount && <Indicator text={formattedFilterCount} />}
                    {expanded ? (
                      <Icon
                        name={Icon.Name.ChevronUp}
                        fill={Colors.ArmyGreen500}
                        size={18}
                      />
                    ) : (
                      <Icon
                        name={Icon.Name.ChevronDown}
                        fill={Colors.ArmyGreen500}
                        size={18}
                      />
                    )}
                  </Box>
                </AccordionButton>
                <AccordionPanel sx={panelStyle}>{children}</AccordionPanel>
              </>
            );
          }}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

export default Dropdown;
